import useMedia from '@/hooks/useMedia'
import { useWindowWidth } from '@react-hook/window-size'
import classnames from 'classnames/bind'
import { isEmpty } from 'lodash-es'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { Autoplay, Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useState } from 'react'

// Libs
import { handleGtmSelectPromotion, handleGtmViewPromotion } from '@/methods/handleGtmEvent'
import { useBannerList } from './hooks/useBannerList'

// Components
import Image from '@/components/common/Image'
import Icon from '@/assets/icons'

// Constants
import { BANNER_IMAGE_SIZE, BANNER_LOCATION } from '@/constants/banner'
import { MAX_MOBILE_WIDTH } from '@/constants/layout'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

const BANNER_IMAGE_DESKTOP_HEIGHT = 320
const BANNER_IMAGE_MOBILE_HEIGHT = 120

interface IBannerProps {
  className?: any
  location: string
  autoplay?: boolean
  imgWrapperClassName?: string
}

const CUSTOM_NAV_BUTTON_CLASS_NAME = {
  prev: 'custom-nav__button-prev',
  next: 'custom-nav__button-next',
}

const Banner = (props: IBannerProps) => {
  const { className, location, autoplay = true, imgWrapperClassName } = props
  const { isDesktop, isMobile, isDesktopM } = useMedia()
  const viewportWidth = useWindowWidth()

  const { bannerList } = useBannerList({
    location: viewportWidth >= MAX_MOBILE_WIDTH ? BANNER_IMAGE_SIZE[`${location}_DESKTOP`] : BANNER_IMAGE_SIZE[`${location}_MOBILE`],
  })
  const [currentSlide, setCurrentSlide] = useState<{ index: number; slideWidth: number }>({ index: 0, slideWidth: 0 })
  const slidePerView = (() => {
    switch (location) {
      case BANNER_LOCATION.HOME:
        return isMobile ? 'auto' : isDesktop && !isDesktopM ? 'auto' : 1.3
      default:
        return 1
    }
  })()

  return (
    <div className={cx({ [className]: !isEmpty(className) && bannerList.isSuccess && bannerList?.data?.length > 0 })}>
      <div className={cx('banner')}>
        {!bannerList.isSuccess && (
          <div className={cx('banner__carousell')}>
            <Skeleton
              style={{ display: 'inline-block' }}
              borderRadius={10}
              height={!isMobile ? BANNER_IMAGE_DESKTOP_HEIGHT : BANNER_IMAGE_MOBILE_HEIGHT}
            />
          </div>
        )}
        {bannerList?.isSuccess && bannerList?.data?.length > 0 && (
          <Swiper
            className={cx('banner__carousell')}
            data-location={location}
            slidesPerView={slidePerView}
            spaceBetween={10}
            loop={true}
            centeredSlides={true}
            modules={[Pagination, Autoplay, Navigation]}
            allowTouchMove={true}
            speed={500}
            breakpoints={{
              1440: {
                spaceBetween: 20,
              },
            }}
            autoplay={
              autoplay
                ? {
                    delay: 5000,
                    disableOnInteraction: false,
                  }
                : false
            }
            onSlideChange={(swiper) => {
              const bannerDataList = bannerList?.data
              const activeBannerData = bannerDataList[swiper.realIndex]
              handleGtmViewPromotion({
                creativeName: activeBannerData.creativeName,
                creativeSlot: activeBannerData.location,
              })
              setCurrentSlide({ index: swiper.realIndex, slideWidth: swiper.slides[swiper.realIndex].clientWidth })
            }}
            onInit={(swiper) => {
              if (bannerList?.data?.length > 0) {
                const bannerDataList = bannerList?.data
                const activeBannerData = bannerDataList[0]
                handleGtmViewPromotion({
                  creativeName: activeBannerData.creativeName,
                  creativeSlot: activeBannerData.location,
                })
                setCurrentSlide({ index: swiper.realIndex, slideWidth: swiper.slides[swiper.realIndex].clientWidth })
              }
            }}
            pagination={{ clickable: true }}
            {...(location === BANNER_LOCATION.HOME && {
              navigation: {
                nextEl: `.${CUSTOM_NAV_BUTTON_CLASS_NAME.next}`,
                prevEl: `.${CUSTOM_NAV_BUTTON_CLASS_NAME.prev}`,
              },
            })}
          >
            {bannerList.data.map((item: any, index: number) => (
              <SwiperSlide key={index} data-location={location}>
                <a
                  href={!isEmpty(item.imgLink) ? item.imgLink : null}
                  target='_blank'
                  rel='noreferrer'
                  onClick={() => {
                    const bannerData = bannerList?.data[index]
                    handleGtmSelectPromotion({
                      creativeName: bannerData.creativeName,
                      creativeSlot: bannerData.location,
                    })
                  }}
                >
                  <div className={cx('banner__image-wrapper', imgWrapperClassName)} data-location={location}>
                    <Image src={item.imgUrl} alt='Banner' width={1000} height={320} style={{ objectFit: 'cover' }} />
                  </div>
                </a>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        {isDesktop && (
          <div
            className={cx('custom-nav')}
            style={{ width: currentSlide.slideWidth, opacity: location === BANNER_LOCATION.HOME && currentSlide.slideWidth > 0 ? 1 : 0 }}
          >
            <button className={`${CUSTOM_NAV_BUTTON_CLASS_NAME.prev} ${cx('custom-nav__button', 'prev')}`}>
              <Icon.ArrowLeft />
            </button>
            <button className={`${CUSTOM_NAV_BUTTON_CLASS_NAME.next} ${cx('custom-nav__button', 'next')}`}>
              <Icon.ArrowLeft />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default Banner
